import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { Plus, Search, Star, Check, X, Edit, Trash2, MessageSquare, Video } from "lucide-react";

interface Review {
  id: string;
  tripId?: string;
  destinationId?: string;
  guideId?: string;
  customerName: string;
  customerLocation?: string;
  rating: number;
  title?: string;
  content: string;
  tripDate?: string;
  isFeatured: boolean;
  isVideo: boolean;
  videoUrl?: string;
  isApproved: boolean;
  createdAt: string;
  tripTitle?: string;
  guideName?: string;
  destinationName?: string;
}

export default function ReviewsPage() {
  const [search, setSearch] = useState("");
  const [filterApproved, setFilterApproved] = useState<"all" | "approved" | "pending">("all");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["reviews"],
    queryFn: () => api.get<ApiResponse<Review[]>>("/api/admin/reviews"),
  });

  const toggleApprovalMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/reviews/${id}/toggle-approval`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/reviews/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });

  const reviews = data?.data || [];

  // Filter reviews
  const filteredReviews = reviews.filter((review) => {
    const matchesSearch =
      review.customerName.toLowerCase().includes(search.toLowerCase()) ||
      review.content.toLowerCase().includes(search.toLowerCase()) ||
      review.title?.toLowerCase().includes(search.toLowerCase()) ||
      review.tripTitle?.toLowerCase().includes(search.toLowerCase());

    const matchesApproved =
      filterApproved === "all" ||
      (filterApproved === "approved" && review.isApproved) ||
      (filterApproved === "pending" && !review.isApproved);

    return matchesSearch && matchesApproved;
  });

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this review?")) {
      deleteMutation.mutate(id);
    }
  };

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        Error loading reviews. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reviews</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage customer reviews</p>
        </div>
        <Link to="/reviews/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          New review
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterApproved("all")}
              className={`btn-sm ${filterApproved === "all" ? "btn-primary" : "btn-ghost"}`}
            >
              All
            </button>
            <button
              onClick={() => setFilterApproved("approved")}
              className={`btn-sm ${filterApproved === "approved" ? "btn-primary" : "btn-ghost"}`}
            >
              <Check className="h-4 w-4" />
              Approved
            </button>
            <button
              onClick={() => setFilterApproved("pending")}
              className={`btn-sm ${filterApproved === "pending" ? "btn-primary" : "btn-ghost"}`}
            >
              <X className="h-4 w-4" />
              Pending
            </button>
          </div>
        </div>
      </div>

      {/* Reviews list */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <MessageSquare className="mb-2 h-12 w-12 text-gray-300" />
          <p>No reviews found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div
              key={review.id}
              className={`card overflow-hidden p-0 ${!review.isApproved ? "border-l-warning-500 border-l-4" : ""}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {review.customerName}
                      </span>
                      {review.customerLocation && (
                        <span className="text-sm text-gray-500">
                          from {review.customerLocation}
                        </span>
                      )}
                      {/* Rating stars */}
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                          />
                        ))}
                      </div>
                      {review.isFeatured && <span className="badge-primary text-xs">Featured</span>}
                      {review.isVideo && (
                        <span className="badge bg-purple-100 text-xs text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          <Video className="mr-1 h-3 w-3" />
                          Video
                        </span>
                      )}
                      {!review.isApproved && (
                        <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400 text-xs">
                          Pending approval
                        </span>
                      )}
                    </div>

                    {review.title && (
                      <h3 className="mt-2 font-medium text-gray-900 dark:text-white">
                        {review.title}
                      </h3>
                    )}

                    <p className="mt-2 line-clamp-2 text-gray-600 dark:text-gray-400">
                      {review.content}
                    </p>

                    <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                      {review.tripTitle && <span>Trip: {review.tripTitle}</span>}
                      {review.guideName && <span>Guide: {review.guideName}</span>}
                      {review.destinationName && <span>Destination: {review.destinationName}</span>}
                      {review.tripDate && (
                        <span>
                          Trip date: {new Date(review.tripDate).toLocaleDateString("en-US")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      onClick={() => toggleApprovalMutation.mutate(review.id)}
                      disabled={toggleApprovalMutation.isPending}
                      className={`btn-sm ${review.isApproved ? "btn-ghost text-warning-600" : "btn-primary"}`}
                      title={review.isApproved ? "Revoke approval" : "Approve"}
                    >
                      {review.isApproved ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                    <Link to={`/reviews/${review.id}`} className="btn-ghost btn-sm">
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(review.id)}
                      disabled={deleteMutation.isPending}
                      className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
